"use client";

import React from "react";
import { Text, SectionCard, CardTitle, Section } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { BarChart } from "@/src/components/charts/bar-chart";

const basicData = {
  categories: ["Q1", "Q2", "Q3", "Q4"],
  series: [
    { name: "Revenue", data: [420, 510, 580, 710] },
    { name: "Expenses", data: [310, 360, 390, 460] },
  ],
};

const horizontalData = {
  categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  series: [
    { name: "Sales", data: [280, 410, 520, 630, 580, 720] },
  ],
};

const stackedData = {
  categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  series: [
    { name: "Revenue", data: [320, 332, 301, 334, 390, 330, 320] },
    { name: "Profit", data: [120, 132, 101, 134, 90, 130, 110] },
    { name: "Expenses", data: [220, 182, 191, 234, 290, 230, 200] },
  ],
};

export default function DocsBarChartPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Bar Chart
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              Compare categorical data with vertical or horizontal bars. Perfect for
              financial comparisons, quarterly breakdowns, and multi-series analysis.
            </Text>
          </div>
        </Section>

        {/* Basic Example */}
        <Section title="Basic Example">
          <SectionCard
            header={
              <>
                <CardTitle>Vertical Bar Chart</CardTitle>
              </>
            }
            contentClassName="p-theme-5"
          >
            <BarChart data={basicData} height={350} showValues={false} />
          </SectionCard>
        </Section>

        {/* Horizontal Variant */}
        <Section title="Variants">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>Horizontal Bar Chart</CardTitle>}
              contentClassName="p-theme-5"
            >
              <BarChart data={horizontalData} height={300} horizontal={true} />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Stacked Bar Chart</CardTitle>}
              contentClassName="p-theme-5"
            >
              <BarChart data={stackedData} height={300} stacked={true} />
            </SectionCard>
          </div>
        </Section>

        {/* With Values */}
        <Section title="With Value Labels">
          <SectionCard
            header={<CardTitle>Bars with Value Display</CardTitle>}
            contentClassName="p-theme-5"
          >
            <BarChart
              data={basicData}
              height={350}
              showValues={true}
              formatValue={(val) => `$${val}k`}
            />
          </SectionCard>
        </Section>

        {/* Props Table */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>BarChartProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">data</td>
                  <td className="py-2 px-2">BarChartData</td>
                  <td className="py-2 px-2">Required</td>
                  <td className="py-2 px-2">Categories and series data</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart subtitle</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">100%</td>
                  <td className="py-2 px-2">Chart height in px or CSS value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">horizontal</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Swap to horizontal orientation</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">stacked</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Stack bars on top of each other</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLegend</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">auto</td>
                  <td className="py-2 px-2">Show legend (auto if more than 1 series)</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showValues</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Display value labels on bars</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">formatValue</td>
                  <td className="py-2 px-2">(value: number) =&gt; string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Format values in labels and tooltip</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">onBarClick</td>
                  <td className="py-2 px-2">(params) =&gt; void</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Click event handler for bars</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Data Structure */}
        <Section title="Data Structure">
          <SectionCard
            header={<CardTitle>BarChartData Interface</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`interface BarChartData {
  /** Category labels for X-axis */
  categories: string[];
  /** One or more data series */
  series: {
    name: string;           // Series name in legend
    data: number[];         // Data points matching categories
    color?: string;         // Optional custom color (hex/CSS)
  }[];
}

// Example:
const data = {
  categories: ["Q1", "Q2", "Q3", "Q4"],
  series: [
    {
      name: "Revenue",
      data: [420, 510, 580, 710],
      color: "#3b82f6"
    },
    {
      name: "Expenses",
      data: [310, 360, 390, 460]
    }
  ]
};`}</code>
            </pre>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsBarChartPage as BarChartPage };
